import {computed, effectScope, shallowRef, watch} from 'vue';
import {useUserStore} from '@navbar-logged/features/user';
import type {NavigationTabsRecord} from './navigation.types';
import {useClientNavigationTabs} from './useClientNavigationTabs';
import {useFreelancerNavigationTabs} from './useFreelancerNavigationTabs';
import {useUmbrellaNavigationTabs} from './useUmbrellaNavigationTabs';

export function useNavigationTabs(): NavigationTabsRecord {
    const userStore = useUserStore();

    const isFreelanceIdentity = computed(() => {
        return userStore.hasSelectedFreelanceIdentity || userStore.cachedIdentityRole === 'FREELANCE';
    });

    const isUmbrellaIdentity = computed(() => {
        return userStore.hasSelectedUmbrellaIdentity || userStore.cachedIdentityRole === 'UMBRELLA';
    });

    const tabsRecord = shallowRef<NavigationTabsRecord>({} as NavigationTabsRecord);
    const tabsScope = effectScope();

    function runEffect() {
        if (isFreelanceIdentity.value) {
            tabsRecord.value = tabsScope.run(() => {
                return useFreelancerNavigationTabs();
            })!;
        } else if (isUmbrellaIdentity.value) {
            tabsRecord.value = tabsScope.run(() => {
                return useUmbrellaNavigationTabs();
            })!;
        } else {
            tabsRecord.value = tabsScope.run(() => {
                return useClientNavigationTabs();
            })!;
        }
    }

    watch(isFreelanceIdentity, runEffect);
    watch(isUmbrellaIdentity, runEffect);
    runEffect();

    const allNavigationRoutes = computed(() => tabsRecord.value?.allNavigationRoutes.value);
    const filteredNavigationRoutes = computed(() => tabsRecord.value?.filteredNavigationRoutes.value);

    const logoNavigationRoute = computed(() => tabsRecord.value?.logoNavigationRoute.value);

    return {
        logoNavigationRoute,
        allNavigationRoutes,
        filteredNavigationRoutes,
    };
}
